/**
 * Joint angle measurement for corrective shape keys and debug visualization.
 *
 * Measures the rotation of specific joints relative to their rest pose,
 * decomposing along a specified axis. This is the input signal that drives
 * corrective shape key weights.
 *
 * Pure logic — no React, no side effects, no allocations in the hot path.
 */

import * as THREE from "three/webgpu";
import { extractAxisAngle } from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Which joint to measure and on which axis. */
export interface JointAngleTarget {
  /** Bone name in the skeleton (e.g., "shinL") */
  boneName: string;
  /** Human-readable label (e.g., "L Shin") */
  label: string;
  /** Local rotation axis to measure */
  axis: "x" | "y" | "z";
}

/** Result of measuring a single joint. */
export interface JointAngle {
  /** Bone name */
  boneName: string;
  /** Human-readable label */
  label: string;
  /** Rotation axis measured */
  axis: "x" | "y" | "z";
  /** Signed angle in radians, delta from rest pose */
  angle: number;
}

// ---------------------------------------------------------------------------
// Default targets — joints where corrective shape keys are needed
// ---------------------------------------------------------------------------

export const CORRECTIVE_JOINT_TARGETS: JointAngleTarget[] = [
  // Shins — knee flex (Z)
  { boneName: "shinL", label: "L Shin", axis: "z" },
  { boneName: "shinR", label: "R Shin", axis: "z" },
  // Forearms — elbow flex (Z)
  { boneName: "forearmL", label: "L Forearm", axis: "z" },
  { boneName: "forearmR", label: "R Forearm", axis: "z" },
  // Upper arms — shoulder forward/backward (Z), inward/outward (X)
  { boneName: "upper_armL", label: "L Sh.Z", axis: "z" },
  { boneName: "upper_armR", label: "R Sh.Z", axis: "z" },
  { boneName: "upper_armL", label: "L Sh.X", axis: "x" },
  { boneName: "upper_armR", label: "R Sh.X", axis: "x" },
  // Thighs — forward/backward (Z), sideways/inward (X)
  { boneName: "thighL", label: "L Thigh.Z", axis: "z" },
  { boneName: "thighR", label: "R Thigh.Z", axis: "z" },
  { boneName: "thighL", label: "L Thigh.X", axis: "x" },
  { boneName: "thighR", label: "R Thigh.X", axis: "x" },
  // Feet — dorsi/plantarflexion (X)
  { boneName: "footL", label: "L Foot", axis: "x" },
  { boneName: "footR", label: "R Foot", axis: "x" },
  // Shoulders (clavicle) — up/down (X)
  { boneName: "shoulderL", label: "L Clav", axis: "x" },
  { boneName: "shoulderR", label: "R Clav", axis: "x" },
  // Hands (wrist) — forward/backward (Z)
  { boneName: "handL", label: "L Wrist", axis: "z" },
  { boneName: "handR", label: "R Wrist", axis: "z" },
  // Hips (center) — backward (X), lateral tilt (Z)
  { boneName: "hips", label: "Hips.X", axis: "x" },
  { boneName: "hips", label: "Hips.Z", axis: "z" },
  // Chest (center) — forward bend (X)
  { boneName: "spine1", label: "Chest.X", axis: "x" },
];

// ---------------------------------------------------------------------------
// Scratch objects — reused each frame to avoid GC pressure
// ---------------------------------------------------------------------------

const _restQ = new THREE.Quaternion();
const _currentQ = new THREE.Quaternion();
const _deltaQ = new THREE.Quaternion();
const _axisVec = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Measurement
// ---------------------------------------------------------------------------

/**
 * Measure joint angles for a set of targets.
 *
 * For each target, computes the delta quaternion from rest pose to current pose,
 * then extracts the signed rotation angle around the specified axis.
 *
 * @param targets - Which joints/axes to measure
 * @param boneMap - Live THREE.Bone references (from boneRegistry)
 * @param restPoses - Rest-pose quaternions [x,y,z,w] per bone name
 * @param bonePoses - Current stored poses [x,y,z,w] per bone name (if any)
 * @returns Measured angles, one per target (targets with missing bones are omitted)
 */
export function measureJointAngles(
  targets: readonly JointAngleTarget[],
  boneMap: Map<string, THREE.Bone>,
  restPoses: Map<string, [number, number, number, number]>,
  bonePoses: Map<string, [number, number, number, number]> | undefined,
): JointAngle[] {
  const results: JointAngle[] = [];

  for (const target of targets) {
    const bone = boneMap.get(target.boneName);
    if (!bone) continue;

    const rest = restPoses.get(target.boneName);
    if (!rest) continue;

    // Current pose: use stored pose if available, otherwise the live bone quaternion
    const stored = bonePoses?.get(target.boneName);
    if (stored) {
      _currentQ.set(stored[0], stored[1], stored[2], stored[3]);
    } else {
      _currentQ.copy(bone.quaternion);
    }

    // Delta from rest: delta = inv(rest) * current
    _restQ.set(rest[0], rest[1], rest[2], rest[3]);
    _deltaQ.copy(_restQ).invert().multiply(_currentQ);
    _deltaQ.normalize();

    // Extract rotation around the target axis
    _axisVec.set(
      target.axis === "x" ? 1 : 0,
      target.axis === "y" ? 1 : 0,
      target.axis === "z" ? 1 : 0,
    );

    const angle = extractAxisAngle(_deltaQ, _axisVec);

    results.push({
      boneName: target.boneName,
      label: target.label,
      axis: target.axis,
      angle,
    });
  }

  return results;
}
